import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Globe, Settings, Type, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNeuralAudio } from "../../lib/audio";
import { Box } from "../atoms/Box";
import { ThemeToggle } from "./ThemeToggle";

const fontSizes = {
  small: "87.5%",
  default: "100%",
  large: "112.5%",
};

export interface SettingsMenuProps {
  className?: string;
}

export const SettingsMenu = ({ className }: SettingsMenuProps) => {
  const [open, setOpen] = React.useState(false);
  const [fontSize, setFontSize] = React.useState<keyof typeof fontSizes>("default");
  const { i18n, t } = useTranslation();
  const { play } = useNeuralAudio();

  React.useEffect(() => {
    const saved = window.localStorage.getItem("font-size");
    if (saved === "small" || saved === "default" || saved === "large") setFontSize(saved);
  }, []);

  const updateFontSize = (size: keyof typeof fontSizes) => {
    setFontSize(size);
    document.documentElement.style.fontSize = fontSizes[size];
    window.localStorage.setItem("font-size", size);
    play("calibrate");
  };

  const updateLanguage = (language: "en" | "es") => {
    void i18n.changeLanguage(language);
    window.localStorage.setItem("i18nextLng", language);
    play("calibrate");
  };

  return (
    <div className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label={t("settings.aria_open")}
        aria-expanded={open}
        className="flex size-10 items-center justify-center rounded-sm border border-border bg-surface text-primary hover:border-border-accent hover:text-accent"
      >
        <Settings size={18} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            className="absolute right-0 top-full z-50 mt-3 w-80"
          >
            <Box variant="glass" padding="md" rounded="xl">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-semibold text-primary">
                    {t("settings.title")}
                  </h2>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label={t("settings.aria_close")}
                    className="text-secondary hover:text-primary"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="space-y-3">
                  <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-tertiary">
                    <Globe size={14} />
                    {t("settings.language")}
                  </p>
                  <div className="flex gap-2">
                    {(["en", "es"] as const).map((language) => (
                      <button
                        key={language}
                        type="button"
                        onClick={() => updateLanguage(language)}
                        className={`rounded-md border px-3 py-2 text-sm ${
                          i18n.language.startsWith(language)
                            ? "border-accent bg-accent-bg text-accent"
                            : "border-border text-secondary"
                        }`}
                      >
                        {t(`lang.${language}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-tertiary">
                    <Type size={14} />
                    {t("settings.font_size")}
                  </p>
                  <div className="flex gap-2">
                    {(Object.keys(fontSizes) as (keyof typeof fontSizes)[]).map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => updateFontSize(size)}
                        className={`rounded-md border px-3 py-2 text-xs ${
                          fontSize === size
                            ? "border-accent bg-accent-bg text-accent"
                            : "border-border text-secondary"
                        }`}
                      >
                        {t(`settings.font_size_${size}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-border-subtle pt-4">
                  <span className="font-mono text-xs uppercase tracking-wider text-tertiary">
                    {t("settings.theme")}
                  </span>
                  <ThemeToggle />
                </div>
              </div>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
